import React, { useState } from 'react';

import classNames from 'classnames';

import { Calendar } from 'wanderu-component-datepicker';
import WanderuErrorBoundary from 'wanderu-component-error-boundary';
import { Redirect } from 'wanderu-component-react-router-link';
import {
    Lightbox,
    Modal,
    WDSButton,
} from 'wanderu-components';
import { localize } from 'wanderu-i18n';
import {
    buildUrlParams,
    buildValidUrl,
    formatDateForSearchUrl,
} from 'wanderu-utils';

import Events from './events/ReturnButtonEvents';

import styles from './ReturnButton.scss';

import type { TFunction } from 'i18next';
import type { MomentInput, Moment } from 'moment';

interface ReturnButtonProps {
    arriveDate?: string;
    className?: string;
    destinationPlaceId?: string;
    destinationText?: string;
    i18nMoment: (date?: MomentInput) => Moment;
    originPlaceId?: string;
    originText?: string;
    t: TFunction;
    tripId?: string;
}

function buildSearchUrl(props: Omit<ReturnButtonProps, 't' | 'i18nMoment'>, returnDate: string): string | undefined {
    const {
        arriveDate,
        destinationPlaceId,
        destinationText,
        originPlaceId,
        originText,
        tripId,
    } = props;

    const params = {
        departDate: formatDateForSearchUrl(arriveDate),
        departTripId: tripId,
        destinationPlaceId,
        destinationText,
        direction: 'return',
        originPlaceId,
        originText,
        returnDate: formatDateForSearchUrl(returnDate),
    };

    const urlParams = buildUrlParams(params);
    return buildValidUrl(urlParams);
}

export const ReturnButton: React.FC<ReturnButtonProps> = ({
    arriveDate = '',
    className = '',
    destinationPlaceId,
    destinationText = '',
    i18nMoment,
    originPlaceId,
    originText = '',
    t,
    tripId = '',
}) => {
    const [show, setShow] = useState(false);
    const [redirect, setRedirect] = useState(false);
    const [date, setDate] = useState(i18nMoment(arriveDate));

    const onDateChange = (e: { target: { value: Moment } }) => {
        const dateSelected = e.target.value;

        Events.addReturnDateSelected({
            dateSelected,
            tripId,
        });

        setRedirect(true);
        setDate(dateSelected);
    };

    const toggleCalendar = () => {
        Events.addReturnTripDropdownClicked({
            tripId,
        });

        setShow(!show);
    };

    if (redirect) {
        return (
            <Redirect
                href={buildSearchUrl(
                    {
                        arriveDate,
                        destinationPlaceId,
                        destinationText,
                        originPlaceId,
                        originText,
                        tripId,
                    },
                    date?.format()
                )}
                push
            />
        );
    }

    return (
        <>
            <WDSButton
                className={classNames(className, styles.button)}
                color="neutral"
                onClick={toggleCalendar}
                variant="outlined"
                fullWidth
            >
                {t('add-return-trip')}
            </WDSButton>

            {show && (
                <Modal>
                    <Lightbox onHide={toggleCalendar} showCloseButton={false}>
                        <WanderuErrorBoundary
                            fallback=""
                            scope="ReturnButton Calendar"
                        >
                            <Calendar
                                calendarClass={styles.calendarContainer}
                                dayHeaderClass={styles.dayHeaderClass}
                                minDate={date}
                                onDateChange={onDateChange}
                                selectedDate={date}
                            />
                        </WanderuErrorBoundary>
                    </Lightbox>
                </Modal>
            )}
        </>
    );
};

export default localize(ReturnButton, ['t', 'i18nMoment'], 'baseApp');
